import { Stack } from '@chakra-ui/layout';
import {
  Text,
  Flex,
  Heading,
  IconButton,
  Button,
  useCheckboxGroup,
  CheckboxGroup,
  Checkbox,
  Textarea,
} from '@chakra-ui/react';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';

import CloseIcon from '@/assets/icons/ic-close.svg';

import { ErrorMessage } from '@/components/core/inputs/ErrorMessage';
import { IToastType } from '@/enums/ToastType';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useStores } from '@/hooks/useStores';
import { useToast } from '@/hooks/useToast';
import { AnyProfile } from '@/utils/profileHelpers';

interface IDeleteChat {
  onClose: () => void;
  profile: AnyProfile;
}

const reportReasons = ['Harassment', 'Inappropriate content', 'Spam', 'Suspicious activity', 'Other'];

interface IReportForm {
  message: string;
}

export const ReportUser: FC<IDeleteChat> = ({ onClose, profile }) => {
  const { store } = useStores();
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });
  const toast = useToast();
  const { value, getCheckboxProps } = useCheckboxGroup({ defaultValue: [] });
  const [isTouched, setIsTouched] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IReportForm>();

  const isInvalid = isTouched && value.length === 0;

  async function onSubmit({ message }: IReportForm) {
    try {
      setIsTouched(true);
      if (value.length === 0) return;

      await store.request(`thin_profiles/${profile.id}/report`, 'POST', {
        data: {
          type: 'user_report',
          id: '',
          attributes: {
            reason: value.join(','),
            event_id: eventId,
            message: message,
          },
        },
      });
      toast({ status: IToastType.INFO, title: 'User has been reported' });

      onClose();
    } catch (error) {
      toast({ status: IToastType.ERROR, title: 'Error' });
    }
  }

  return (
    <Flex
      backgroundColor="white"
      zIndex="10"
      position="absolute"
      top={0}
      bottom={0}
      left={0}
      right={0}
      flexDirection="column"
      justifyContent="center"
      alignItems="flex-start"
      p={6}
      w="full"
      as="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Flex w="full" flexDirection="row" justifyContent="space-between" alignItems="center">
        <Heading fontSize="2xl">Contact support</Heading>
        <IconButton
          size="xs"
          onClick={() => onClose()}
          aria-label="Close"
          ml="2"
          variant="unstyled"
          icon={<CloseIcon width="24px" />}
        />
      </Flex>
      <Text mt={4}>Reason for reporting:</Text>
      <CheckboxGroup colorScheme="violet">
        <Stack my={3} spacing={6} direction="column">
          {reportReasons.map((key) => (
            <Checkbox
              lineHeight={1}
              spacing={4}
              size="lg"
              color="black"
              key={key}
              {...getCheckboxProps({ value: key })}
            >
              {key}
            </Checkbox>
          ))}
        </Stack>
      </CheckboxGroup>
      {isInvalid && (
        <Text fontFamily="heading" color="red.700" fontSize="sm">
          Please select at least one reason for reporting
        </Text>
      )}
      <Textarea
        placeholder="Write a message"
        mt={2}
        {...register('message', { required: { value: true, message: 'Please describe the issue in your message' } })}
        aria-invalid={Boolean(errors.message) || undefined}
        height="100%"
      />
      <ErrorMessage errors={errors} name="message" />
      <Flex w="full" mt={4} justifyContent="space-between">
        <Button type="submit" size="sm">
          Report user
        </Button>
        <Button onClick={() => onClose()} size="sm" variant="black">
          Cancel
        </Button>
      </Flex>
    </Flex>
  );
};
